import { useEffect, useState, useCallback } from "react";
import { Plus, Trash, PencilSimple, Target, Warning, ChartBar } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Cell } from "recharts";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EXPENSE_CATS = ["Maaş", "Kira", "Tedarik", "Elektrik/Su", "Pazarlama", "Vergi", "Lojistik", "Ödeme", "Diğer"];
const empty = { category: "Kira", limit: "", currency: "TRY" };

export default function Budgets() {
  const { currencies } = useCurrency();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [trend, setTrend] = useState(null);
  const [trendOpen, setTrendOpen] = useState(false);

  const openTrend = async (b) => {
    setTrend(null);
    setTrendOpen(true);
    try {
      const { data } = await api.get(`/budgets/${b.id}/trend`);
      setTrend(data);
    } catch (e) { toast.error("Trend yüklenemedi"); }
  };

  const load = useCallback(() => {
    api.get("/budgets").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ category: b.category, limit: String(b.limit), currency: b.currency }); setEditId(b.id); setOpen(true); };

  const submit = async () => {
    if (!form.limit || Number(form.limit) <= 0) return toast.error("Geçerli bir limit girin");
    const payload = { ...form, limit: Number(form.limit) };
    try {
      if (editId) await api.put(`/budgets/${editId}`, payload);
      else await api.post("/budgets", payload);
      toast.success(editId ? "Bütçe güncellendi" : "Bütçe eklendi");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const remove = async (id) => { await api.delete(`/budgets/${id}`); toast.success("Silindi"); load(); };

  const overCount = items.filter((b) => b.over).length;

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light tracking-tight">Bütçe Hedefleri</h1>
          <p className="text-sm text-muted-foreground mt-1">Kategori bazlı aylık harcama limitleri belirleyin, aşımda uyarı alın</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-budget-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus size={18} className="mr-1" /> Yeni Bütçe
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading">{editId ? "Bütçeyi Düzenle" : "Yeni Bütçe Hedefi"}</DialogTitle>
              <DialogDescription className="text-muted-foreground">Bir kategori için aylık harcama limiti tanımlayın.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Kategori</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)} disabled={!!editId}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="budget-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Aylık Limit</Label>
                  <Input type="number" value={form.limit} onChange={(e) => set("limit", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="budget-limit" placeholder="0" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Para Birimi</Label>
                  <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="budget-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} data-testid="budget-save" className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {overCount > 0 && (
        <div data-testid="budget-alert" className="flex items-center gap-3 border border-destructive/40 bg-destructive/10 rounded-lg p-4">
          <Warning size={22} className="text-destructive" weight="fill" />
          <span className="text-sm text-destructive">{overCount} kategoride bütçe limiti aşıldı. Harcamalarınızı gözden geçirin.</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <p className="text-sm text-muted-foreground">Henüz bütçe hedefi tanımlanmadı.</p>}
        {items.map((b) => {
          const pct = Math.min(b.percent, 100);
          const near = b.percent >= 80 && !b.over;
          const color = b.over ? "#EF4444" : near ? "#D4AF37" : "#10B981";
          return (
            <div key={b.id} data-testid={`budget-card-${b.id}`} className={`bg-card border rounded-lg p-6 transition-transform hover:-translate-y-1 ${b.over ? "border-destructive/50" : "border-border"}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                    <Target size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="text-sm font-medium">{b.category}</div>
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{b.currency} · Aylık</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openTrend(b)} data-testid={`budget-trend-${b.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1" title="Trend"><ChartBar size={16} /></button>
                  <button onClick={() => openEdit(b)} data-testid={`budget-edit-${b.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1"><PencilSimple size={16} /></button>
                  <button onClick={() => remove(b.id)} data-testid={`budget-delete-${b.id}`} className="text-muted-foreground hover:text-destructive transition-colors p-1"><Trash size={16} /></button>
                </div>
              </div>

              <div className="mt-5 flex items-end justify-between">
                <span className="font-mono-num text-lg font-semibold" style={{ color }}>{formatMoney(b.spent, b.currency, currencies)}</span>
                <span className="text-xs text-muted-foreground font-mono-num">/ {formatMoney(b.limit, b.currency, currencies)}</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs" style={{ color }}>{b.percent}% kullanıldı</span>
                {b.over ? (
                  <span className="text-xs text-destructive flex items-center gap-1"><Warning size={13} weight="fill" /> {formatMoney(Math.abs(b.remaining), b.currency, currencies)} aşıldı</span>
                ) : (
                  <span className="text-xs text-muted-foreground">{formatMoney(b.remaining, b.currency, currencies)} kaldı</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={trendOpen} onOpenChange={setTrendOpen}>
        <DialogContent className="bg-card border-border max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Bütçe Trendi{trend ? ` — ${trend.category}` : ""}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Son 6 ayın harcaması ve aylık limit karşılaştırması.</DialogDescription>
          </DialogHeader>
          {!trend ? (
            <div className="py-10 flex justify-center">
              <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            </div>
          ) : (
            <div className="h-72" data-testid="budget-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
                  <XAxis dataKey="month" stroke="#A1A1AA" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="#A1A1AA" fontSize={12} tickLine={false} axisLine={false}
                    tickFormatter={(v) => new Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
                  <Tooltip contentStyle={{ background: "#18181B", border: "1px solid #27272A", borderRadius: 8, fontSize: 13 }}
                    formatter={(v) => [formatMoney(v, trend.currency, currencies), "Harcanan"]} cursor={{ fill: "#ffffff08" }} />
                  <ReferenceLine y={trend.limit} stroke="#D4AF37" strokeDasharray="5 4"
                    label={{ value: `Limit ${formatMoney(trend.limit, trend.currency, currencies)}`, fill: "#D4AF37", fontSize: 11, position: "insideTopRight" }} />
                  <Bar dataKey="spent" radius={[3, 3, 0, 0]}>
                    {trend.trend.map((m, i) => (
                      <Cell key={i} fill={m.spent > trend.limit ? "#EF4444" : "#10B981"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
